<script setup lang="ts">
type Props = {
  type: 'success' | 'error' | 'incomplete';
  successText?: string;
  errorText?: string;
  incompleteText?: string;
  showClose?: boolean;
  /** tempo até fechar (ms) */
  autoCloseMs?: number;
};

const {
  type,
  successText = 'concluída com sucesso!',
  errorText = 'não tem saldo suficiente para adquirir o item.',
  incompleteText = 'compra incompleta.',
  showClose = true,
  autoCloseMs = 3000,
} = defineProps<Props>();

const emit = defineEmits<{ (e: 'close'): void }>();

let autoTimer: ReturnType<typeof setTimeout> | undefined;
let progressTimer: ReturnType<typeof setInterval> | undefined;

const progress = ref(100); // 100% → 0%
const barColor = computed(() =>
  type === 'success'
    ? '#4ADE80'
    : type === 'incomplete'
      ? '#FF9D00'
      : '#FF3B30',
);

const formattedIncomplete = computed(() => {
  if (type !== 'incomplete')
    return { before: incompleteText, highlight: '', after: '' };

  // Formato esperado: "Você conseguiu adquirir|01 de 02 itens."
  const parts = incompleteText.split('|');

  if (parts.length === 2) {
    return {
      before: parts[0], // "Você conseguiu adquirir"
      highlight: parts[1], // "01 de 02 itens."
      after: '',
    };
  }

  // Fallback
  return { before: incompleteText, highlight: '', after: '' };
});

onMounted(() => {
  const start = Date.now();

  // timer de fechamento
  autoTimer = setTimeout(() => emit('close'), autoCloseMs);

  // atualiza a barrinha ~60fps
  progressTimer = setInterval(() => {
    const elapsed = Date.now() - start;
    const pct = Math.max(0, 100 - (elapsed / autoCloseMs) * 100);

    progress.value = pct;
    if (pct <= 0) {
      clearInterval(progressTimer!);
      progressTimer = undefined;
    }
  }, 16);
});

onUnmounted(() => {
  if (autoTimer) clearTimeout(autoTimer);
  if (progressTimer) clearInterval(progressTimer);
});

const formattedError = computed(() => {
  let text = errorText;
  const highlightPatterns = [
    /insuficientes para comprar/gi,
    /limite de compra no dia/gi,
  ];

  for (const pattern of highlightPatterns) {
    text = text.replace(
      pattern,
      '<span class="text-[#FF3B30] font-bold">$&</span>',
    );
  }

  return text;
});
</script>

<template>
  <div
    class="w-[350px] p-7 pb-9 relative bg-gray-950 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-800 backdrop-blur-[10px] inline-flex flex-col justify-start items-start gap-3.5"
  >
    <button
      v-if="showClose"
      aria-label="Fechar"
      class="absolute top-2 right-2 z-20 w-[32px] h-[32px] text-white/70 hover:text-white transition-colors flex justify-center items-center"
      type="button"
      @click="emit('close')"
    >
      <NuxtIcon
        class="font-bold text-[22px]"
        name="close"
      />
    </button>

    <div
      class="self-stretch py-5 inline-flex flex-col justify-start items-center gap-6"
    >
      <div class="relative w-16 h-16 flex justify-center items-center">
        <NuxtIcon
          class="text-[80px] select-none"
          :class="
            type === 'success'
              ? 'text-[#4ADE80]'
              : type === 'incomplete'
                ? 'text-[#FF9D00]'
                : 'text-[#FF3B30]'
          "
          :filled="true"
          :name="
            type === 'success'
              ? 'store/success'
              : type === 'incomplete'
                ? 'store/incomplete'
                : 'store/error'
          "
        />
      </div>

      <div class="w-full flex flex-col items-center gap-2 font-sans">
        <div class="w-72 text-center leading-snug text-lg text-white font-bold">
          <template v-if="type === 'success'">
            <span class="text-[#4ADE80]">{{ ' ' + successText }}</span>
          </template>
          <template v-else-if="type === 'incomplete'">
            <span class="text-white">{{ formattedIncomplete.before }}</span>
            <span
              v-if="formattedIncomplete.highlight"
              class="text-[#FF9D00] font-bold"
              >{{ formattedIncomplete.highlight }}</span
            >
            <span
              v-if="formattedIncomplete.after"
              class="text-white"
              >{{ formattedIncomplete.after }}</span
            >
          </template>
          <template v-else>
            <span v-html="formattedError" />
          </template>
        </div>
      </div>
    </div>

    <!-- ponteiro/balão -->
    <div class="absolute left-[223px] top-[14px]">
      <div
        class="w-3.5 h-3.5 rotate-45 bg-gray-950 outline outline-1 outline-neutral-800 backdrop-blur-[10px]"
      ></div>
    </div>

    <!-- barra de progresso (rodapé) -->
    <div class="absolute left-0 right-0 bottom-0 px-4 pb-3">
      <div class="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          aria-hidden="true"
          class="h-full rounded-full transition-[width] duration-100 linear"
          :style="{ width: progress + '%', backgroundColor: barColor }"
        />
      </div>
      <!-- acessibilidade opcional: texto invisível com o tempo restante -->
      <span class="sr-only"
        >Fechando em progresso: {{ Math.round(progress) }}%</span
      >
    </div>
  </div>
</template>
